import { useEffect, useId, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Message as MessageType } from '../lib/api';
import { detectMediaKind, parseFileMessage } from '../lib/api';
import { useMessageActions } from '../hooks/useMessageActions';
import type { SenderIdentity } from '../lib/spaceIdentity';
import { Lightbox } from './Lightbox';
import { MessageMeta } from './MessageMeta';

interface MessageProps {
  message: MessageType;
  onReply?: (message: MessageType) => void;
  /**
   * Authenticated user's id. Enables the Delete action on the user's own
   * messages. Optional (defaults to none) so existing Message unit tests
   * don't need to wrap in a QueryClientProvider / fetch `/api/me` — see #1257.
   */
  currentUserId?: string;
  /** Resolved sender identity (display name + glyph) for the byline. Absent ⇒
   *  the raw handle, as before. */
  sender?: SenderIdentity;
}

export function Message({ message, onReply, currentUserId, sender }: MessageProps) {
  const fileBody = parseFileMessage(message.content);
  const menuId = useId();
  const {
    openMenuId,
    openMenu,
    closeMenu,
    canDelete,
    copyText,
    copiedId,
    copyFailedId,
    deleteMessage,
    isDeleted,
  } = useMessageActions({
    roomId: message.room_id,
    currentUserId: currentUserId ?? '',
  });

  const menuOpen = openMenuId === message.message_id;
  const justCopied = copiedId === message.message_id;
  const copyFailed = copyFailedId === message.message_id;
  const showDelete = canDelete(message.sender_user_id);

  // Escape closes the open action menu (mirrors BurgerMenu's keyboard close).
  useEffect(() => {
    if (!menuOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') closeMenu();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen, closeMenu]);

  if (isDeleted(message.message_id)) {
    return (
      <li
        className="message message--deleted"
        data-persona={message.sender_persona}
      >
        <p className="message-tombstone" role="note">
          Message deleted
        </p>
      </li>
    );
  }

  const senderLabel = sender?.name ?? message.sender_persona;

  return (
    <li className="message" data-persona={message.sender_persona}>
      <MessageMeta message={message} sender={sender} />
      {message.parent_id && (
        <div className="message-reply-indicator" aria-label="In reply to a previous message">
          ↩ reply
        </div>
      )}
      {fileBody ? (
        <FileBody url={fileBody.url} name={fileBody.name} />
      ) : (
        <div className="message-content">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
        </div>
      )}
      {message.attachments.length > 0 && (
        <ul className="message-attachments" aria-label="Attachments">
          {message.attachments.map((att) => (
            <li key={att.attachment_id} className="message-attachment">
              <a href={`/api/attachments/${att.attachment_id}`}>
                {att.filename} ({formatBytes(att.byte_size)})
              </a>
            </li>
          ))}
        </ul>
      )}
      {/* The action bar rests hidden and reveals on hover OR keyboard focus
          (focus-within), the Slack/Discord pattern — 26 messages no longer show
          52 permanent buttons. `data-open` keeps it up while its menu is open. */}
      <div className="message-action-bar" data-open={menuOpen || undefined}>
        {onReply && (
          <button
            type="button"
            className="message-reply-btn btn-secondary"
            onClick={() => onReply(message)}
            aria-label={`Reply to ${senderLabel}`}
          >
            ↩ Reply
          </button>
        )}
        <div className="message-actions">
          <button
            type="button"
            className="message-actions-toggle"
            aria-label={`Actions for message from ${senderLabel}`}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-controls={menuId}
            onClick={() => (menuOpen ? closeMenu() : openMenu(message.message_id))}
          >
            ⋯
          </button>
          {menuOpen && (
            <div id={menuId} className="message-actions-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="message-actions-item"
                onClick={() => {
                  void copyText(message.message_id, message.content);
                }}
              >
                Copy text
              </button>
              {showDelete && (
                <button
                  type="button"
                  role="menuitem"
                  className="message-actions-item message-actions-item--danger"
                  onClick={() => deleteMessage(message.message_id)}
                >
                  Delete
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {/* Copy status lives OUTSIDE the reveal bar: after "Copy text" closes the
          menu, focus can leave and hide the bar — the confirmation must not. */}
      {justCopied && (
        <span className="message-actions-copied" role="status" aria-live="polite">
          Copied!
        </span>
      )}
      {copyFailed && (
        <span
          className="message-actions-copied message-actions-copy-failed"
          role="alert"
        >
          Copy failed — select the text to copy manually.
        </span>
      )}
    </li>
  );
}

interface FileBodyProps {
  url: string;
  name: string;
}

function FileBody({ url, name }: FileBodyProps) {
  const [lightbox, setLightbox] = useState(false);
  const kind = detectMediaKind(name);

  if (kind === 'image') {
    return (
      <>
        <button
          type="button"
          className="message-image-button"
          onClick={() => setLightbox(true)}
          aria-label={`Open ${name} fullscreen`}
        >
          <img src={url} alt={name} className="message-image-thumb" loading="lazy" />
        </button>
        {lightbox && <Lightbox src={url} alt={name} onClose={() => setLightbox(false)} />}
      </>
    );
  }

  if (kind === 'video') {
    return (
      <video className="message-video" controls preload="metadata" src={url}>
        <a href={url}>{name}</a>
      </video>
    );
  }

  return (
    <div className="message-file-link">
      <a href={url} download={name} target="_blank" rel="noopener noreferrer">
        {name}
      </a>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
